import { useEffect, useState, useContext } from "react";
import { Redirect, useLocation } from "react-router-dom";
import OtpVerificationAccount from "../../../components/Form/OtpVerificationAccount";
import useFetch from "../../../hooks/useFetch";
import backendRoutes from "../../../utils/backendRoutes";
import { setError, setUser } from "../../../state/reducers/authSlice";
import { useAppDispatch } from "../../../hooks/useSelector";
import LoadingScreen from "../../../components/LoadingScreen";
import SessionContext from "../../../contexts/SessionContext";
import ToastContext from "../../../contexts/ToastContext";
import { useAuth } from "../../../contexts/AuthContext";
import {
  clearMfaEmail,
  getMfaEmail,
  MfaLocationState,
} from "../../../utils/mfaLogin";

// Second sign-in step: the password was accepted and an OTP was emailed.
// Sending email + OTP to the same endpoint sets the access_token cookie.
const VerifyOtp = () => {
  const dispatch = useAppDispatch();
  const location = useLocation<MfaLocationState | undefined>();
  const [email] = useState(() => getMfaEmail(location.state));
  const [otp, setOtp] = useState("");
  const [verifyPostData, verifyRes, verifyErr, isLoading] = useFetch();
  const [startingSession, setStartingSession] = useState(false);

  // Context
  const { setIsExpired } = useContext(SessionContext);
  const { openError } = useContext(ToastContext);
  const { refreshUser } = useAuth();

  // Keep ?redirect= so ProtectedAuthRoute still sends the user back there.
  const signInLink = `/signin${location.search}`;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      openError("Enter the 6-digit code sent to your email");
      return;
    }
    verifyPostData({
      url: backendRoutes.authenticate,
      data: { email, otp },
      method: "POST",
    });
  };

  // Same as the sign-in page: the session is read back from /api/Account/me,
  // then ProtectedAuthRoute moves the user on to ?redirect= (or "/").
  const setLoggedInUser = async (user: any) => {
    setStartingSession(true);
    setIsExpired(false);
    dispatch(setUser({ ...user }));
    const sessionUser = await refreshUser();
    if (sessionUser) {
      clearMfaEmail();
    } else {
      setStartingSession(false);
      openError("Signed in, but your session could not be started. Please try again.");
    }
  };

  useEffect(() => {
    if (verifyRes) {
      if (!verifyErr && verifyRes?.succeeded) {
        localStorage.setItem("id", verifyRes.data.id);
        setLoggedInUser(verifyRes.data);
      } else {
        setOtp("");
        openError(
          verifyErr?.response?.data?.Message ||
            verifyErr?.response?.data?.message ||
            "An unknown error occured while verifying your code",
        );
        dispatch(setError());
      }
    }
  }, [verifyRes]);

  // Opened directly, or the tab lost the email: start sign-in again.
  if (!email) {
    return <Redirect to={signInLink} />;
  }

  return (
    <>
      {(isLoading || startingSession) && <LoadingScreen />}
      <OtpVerificationAccount
        email={email}
        otp={otp}
        handleChange={handleChange}
        onSubmit={handleSubmit}
        loading={isLoading || startingSession}
        resendLink={signInLink}
      />
    </>
  );
};

export default VerifyOtp;
